import type React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface BottomSheetShellProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export const BottomSheetShell: React.FC<BottomSheetShellProps> = ({
  open,
  onClose,
  children,
}) => {
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 300);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-40">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
        style={{
          animation: closing
            ? 'swipe-hint-out 300ms ease-in-out forwards'
            : 'backdrop-fade-in 300ms ease-in-out forwards',
          WebkitAnimation: closing
            ? 'swipe-hint-out 300ms ease-in-out forwards'
            : 'backdrop-fade-in 300ms ease-in-out forwards',
        }}
      />

      {/* Sheet */}
      <div
        className="absolute bottom-0 left-0 right-0 rounded-t-2xl bg-white px-6 pb-8 pt-5"
        style={{
          animation: closing
            ? 'bottom-sheet-up 300ms ease-in-out reverse forwards'
            : 'bottom-sheet-up 300ms ease-in-out forwards',
          WebkitAnimation: closing
            ? 'bottom-sheet-up 300ms ease-in-out reverse forwards'
            : 'bottom-sheet-up 300ms ease-in-out forwards',
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-full p-1 text-slate-400 hover:text-slate-600"
        >
          <X className="h-5 w-5" />
        </button>

        {children}
      </div>
    </div>,
    document.body,
  );
};
